import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BehaviorSubject, EMPTY, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { OccupationService } from '@cpd-app/core-module/services/occupation';
import { OccupationDetail } from '@cpd-app/core-module/models';
import { ErrorReceived } from '@cpd-app/state/error/error.actions';
import { GoalFacade } from '@cpd-app/state/goal';
import { AuthFacade } from '@cpd-app/state/auth/auth.facade';
import { untilSessionToken } from '@cpd-app/state/auth/until-session-token';

import { UserProfileFacade } from '../user-profile';
import {
  putPreferredLocationSuccess,
  deletePreferredLocationSuccess
} from '../hot-spots/hot-spots.actions';
import * as OccupationDetailActions from './occupation-detail.actions';
import { OccupationDetailFacade } from './occupation-detail.facade';

@Injectable()
export class OccupationDetailEffects {
  constructor(
    private actions$: Actions,
    private occupationService: OccupationService,
    private goalFacade: GoalFacade,
    private userProfileFacade: UserProfileFacade,
    private occupationDetailFacade: OccupationDetailFacade
  ) {}

  /**
   * Release scan, 1 Oct. A NEW VISITOR OPENING AN OCCUPATION LINK GOT "WE
   * COULDN'T LOAD THIS OCCUPATION".
   *
   * On a cold load the route resolver asks for the occupation straight away,
   * while the guest session (GetSession) is still on its way. The request went
   * out with no Authorization header and the server answered "Forbidden
   * resource" as a normal 200, which the page reads as a load error. Going
   * home first worked only because the session had landed by then.
   *
   * The read now waits for the session token, as the notification feed does
   * (#1844): one attempt after 3s without one. If that attempt was sent with
   * no token and came back with errors, it is asked once more when the token
   * lands. With a token already in place, nothing waits.
   */
  private readonly authFacade = inject(AuthFacade);

  loadOccupationDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OccupationDetailActions.loadOccupationDetail),
      withLatestFrom(this.userProfileFacade.userProfileLoadIfEmpty$),
      filter(([action]) => !!action.onetCode),
      switchMap(([action]) => {
        const page = {
          onetCode: action.onetCode,
          jobId: action.jobId ?? undefined
        };
        const fetch = () =>
          this.occupationService.getOccupationDetailResult(
            action.onetCode,
            action.jobId,
            action.jobTitle
          );
        this.shownPage$.next(null);
        return untilSessionToken(this.authFacade.authToken$, 3000).pipe(
          switchMap((token) =>
            fetch().pipe(
              switchMap((result) =>
                !token && !result.occupation && result.hasErrors
                  ? untilSessionToken(this.authFacade.authToken$, 5000).pipe(
                      switchMap((late) =>
                        late
                          ? fetch().pipe(map((r) => ({ ...r, token: late })))
                          : of({ ...result, token })
                      )
                    )
                  : of({ ...result, token })
              )
            )
          ),
          // Which session this page was read under (21 Oct). See
          // refetchUnderNewSession$.
          tap(({ occupation, token }) => {
            if (occupation && token) {
              this.shownPage$.next({
                onetCode: action.onetCode,
                jobId: action.jobId ?? undefined,
                jobTitle: action.jobTitle ?? undefined,
                token
              });
            }
          }),
          /*
           * Three outcomes, not two (code review, 30 Sep). A server error
           * came back as data null under errorPolicy 'all' and was read as
           * "no such occupation", so a valid code showed the not-found page.
           */
          map(({ occupation, hasErrors }) =>
            occupation
              ? OccupationDetailActions.occupationDetailWithBusinessRulesReady({
                  occupationDetail: occupation
                })
              : hasErrors
                ? OccupationDetailActions.loadOccupationError(page)
                : OccupationDetailActions.loadOccupationFailed(page)
          ),
          // A failed request still reports through ErrorReceived as before,
          // and now also tells the page, whose resolver waits on it.
          catchError((error: TypeError) =>
            of(
              ErrorReceived({ error }),
              OccupationDetailActions.loadOccupationError(page)
            )
          )
        );
      })
    )
  );

  /**
   * Launch prep, 21 Oct 2026. A SIGNED-IN LEARNER SAW "N/A" ON ALL FOUR CHIPS.
   *
   * A window with no session token in sessionStorage (a new window, a link
   * from an email, a bookmark) is signed in to Cognito but holds no CPD
   * session yet. CognitoAuthEffects mints an anonymous GUEST session, then
   * links it to the account, and the link issues the member's own token. The
   * read above waits only for SOME token, so it could go out with the
   * guest's, and a guest has no match scores. The profile then loaded as the
   * member, so the page read "Your surveys are in, but this occupation has no
   * scores yet" while the Goals Report rated the same goal Great.
   *
   * So the page remembers the session it was read under and is read again
   * when that session is replaced. A token refresh for the same member also
   * replaces the token; that costs one extra read and shows the same thing.
   * switchMap, so a newer page or session cancels a read still in flight.
   */
  private readonly shownPage$ = new BehaviorSubject<{
    onetCode: string;
    jobId?: string;
    jobTitle?: string;
    token: string;
  } | null>(null);

  refetchUnderNewSession$ = createEffect(() =>
    combineLatest([
      this.shownPage$,
      this.authFacade.authToken$.pipe(distinctUntilChanged())
    ]).pipe(
      switchMap(([page, token]) =>
        !page || !token || token === page.token
          ? EMPTY
          : this.occupationService
              .getOccupationDetailResult(page.onetCode, page.jobId, page.jobTitle)
              .pipe(
                map(({ occupation }) => {
                  if (!occupation) {
                    return OccupationDetailActions.reloadOccupationDetailsFailed();
                  }
                  // Recorded in place: emitting here would switch away from
                  // this read before its result got out.
                  page.token = token;
                  return OccupationDetailActions.reloadOccupationDetailsSuccess({
                    occupationDetail: occupation
                  });
                }),
                catchError(() =>
                  of(OccupationDetailActions.reloadOccupationDetailsFailed())
                )
              )
      )
    )
  );

  /** A page the member has left is not read again for them. */
  forgetShownPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          OccupationDetailActions.clearOccupationDetail,
          OccupationDetailActions.clearOccupationDetailFor
        ),
        tap(() => this.shownPage$.next(null))
      ),
    { dispatch: false }
  );

  reloadOccupationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putPreferredLocationSuccess, deletePreferredLocationSuccess),
      withLatestFrom(this.occupationDetailFacade.occupationDetail$),
      switchMap(([, occupationDetails]) =>
        this.occupationService
          .getOccupationDetail(
            occupationDetails.onetCode,
            occupationDetails.jobId?.toString(),
            occupationDetails.title
          )
          .pipe(
            // Its own failure: a background refresh of the page already shown
            // must not mark whatever page is shown now as not found.
            map((occupationDetail: OccupationDetail) =>
              occupationDetail
                ? OccupationDetailActions.reloadOccupationDetailsSuccess({
                    occupationDetail
                  })
                : OccupationDetailActions.reloadOccupationDetailsFailed()
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  getOccupationDetailsForExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OccupationDetailActions.loadOccupationForExportPdf),
      withLatestFrom(
        this.goalFacade.goals$,
        this.occupationDetailFacade.occupationForExportPdf$
      ),
      filter(
        ([, goals, occupation]) =>
          !!goals?.onetCode &&
          (!occupation || goals.onetCode !== occupation.onetCode)
      ),
      switchMap(([, goals]) =>
        this.occupationService
          .getOccupationForExportPdf(goals.onetCode, goals.jobId)
          .pipe(
            map((occupation) =>
              OccupationDetailActions.loadOccupationForExportPdfSuccess({
                occupation
              })
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );
}
